import { Signal } from "../core/Signal";
import type { AudioEngine } from "./AudioEngine";

export interface BeatConfig {
  bpm?: number;
  swing?: number;
  kickGain?: number;
  snareGain?: number;
  hatGain?: number;
  kickPunch?: number;
}

export interface BeatStep {
  step: number;     // 0..15 in 16 sixteenths
  time: number;     // AudioContext time when this step plays
  bpm: number;      // BPM at scheduling time
  isKick: boolean;
  isSnare: boolean;
  isBarStart: boolean;
}

// Drum sections recognised by the kit. The active section is set
// externally by the Composer via setSection() so the drums and the
// arrangement stay in lock-step.
export type DrumSectionKind =
  | "intro"
  | "verse"
  | "build"
  | "drop"
  | "bridge"
  | "outro";

const SCHEDULE_AHEAD = 0.15;
const LOOKAHEAD_MS = 25;

// 16-step (one bar of half-time) drum patterns per section. Each entry
// describes the trigger at that step:
//   K = kick (loud), k = ghost kick (soft)
//   S = snare (loud)
//   H = hat (loud), h = soft hat
//   . = silence
// Patterns are designed for 140 BPM half-time-felt grooves so the kicks
// land on 1 and 3, snares on 2 and 4, with the busier "build" and
// "drop" sections adding 8th/16th hats and drum fills.
type Trig = "K" | "k" | "S" | "H" | "h" | ".";
interface DrumPattern {
  kicks: Trig[];   // length 16
  snares: Trig[];
  hats: Trig[];
  // Whether to add the ramping snare roll on every 4th bar (dramatic
  // build only). Keeps the whole song from snare-rolling constantly.
  rollOnEvery4thBar?: boolean;
}

const PATTERNS: Record<DrumSectionKind, DrumPattern> = {
  intro: {
    // Sparse: a single kick on 1, soft hats every 4th step, no snare.
    kicks:  ["K",".",".",".",".",".",".",".",".",".",".",".",".",".",".","."],
    snares: [".",".",".",".",".",".",".",".",".",".",".",".",".",".",".","."],
    hats:   [".",".",".",".","h",".",".",".","h",".",".",".","h",".",".","."],
  },
  verse: {
    // Half-time classic: kick on 1+3, snare on 2+4, hats on every 4th step.
    kicks:  ["K",".",".",".",".",".",".",".","K",".",".",".",".",".",".","."],
    snares: [".",".",".",".","S",".",".",".",".",".",".",".","S",".",".","."],
    hats:   ["h",".",".",".","h",".",".",".","h",".",".",".","h",".",".","."],
  },
  build: {
    // Energy ramp: full backbeat + 16th hats. The roll-on-4th-bar adds
    // a snare 16ths roll into the next section -- the drop tease.
    kicks:  ["K",".",".",".",".",".",".",".","K",".",".",".",".",".",".","k"],
    snares: [".",".",".",".","S",".",".",".",".",".",".",".","S",".",".","."],
    hats:   ["H","h","H","h","H","h","H","h","H","h","H","h","H","h","H","h"],
    rollOnEvery4thBar: true,
  },
  drop: {
    // Big half-time feel: heavy kick on 1+3, fat snare on 2+4, ghost
    // kick on 14, 8th-note hats. This is the "drop" beat.
    kicks:  ["K",".",".",".",".",".",".",".","K",".",".",".",".",".","k","."],
    snares: [".",".",".",".","S",".",".",".",".",".",".",".","S",".",".","."],
    hats:   ["H",".","h",".","H",".","h",".","H",".","h",".","H",".","h","."],
  },
  bridge: {
    // Sparse breakdown: only kicks + the occasional soft hat.
    kicks:  ["K",".",".",".",".",".",".",".","K",".",".",".",".",".",".","."],
    snares: [".",".",".",".",".",".",".",".",".",".",".",".",".",".",".","."],
    hats:   [".",".",".","h",".",".",".","h",".",".",".","h",".",".",".","h"],
  },
  outro: {
    // Fading: just kicks 1+3 and a soft hat trail.
    kicks:  ["K",".",".",".",".",".",".",".","k",".",".",".",".",".",".","."],
    snares: [".",".",".",".","S",".",".",".",".",".",".",".",".",".",".","."],
    hats:   ["h",".",".",".",".",".",".",".","h",".",".",".",".",".",".","."],
  },
};

// ProceduralBeat = the metronome + drum kit. It schedules drum hits
// ahead of audio time and exposes:
//   - onStep   : per-16th heartbeat for melodic layers / Composer
//   - setSection(kind) : switch the live drum pattern (Composer call)
//   - triggerCrash(time?) : one-shot cymbal crash for section boundaries
export class ProceduralBeat {
  readonly onStep = new Signal<BeatStep>();

  private engine: AudioEngine;
  private bpm = 140;
  private swing = 0;
  private kickGain = 1;
  private snareGain = 0.7;
  private hatGain = 0.35;
  private kickPunch = 1; // multiplier on kick decay length
  private noiseBuffer: AudioBuffer | null = null;
  private nextNoteTime = 0;
  private step = 0;
  private barCount = 0;
  private timer: number | null = null;
  private playing = false;
  private section: DrumSectionKind = "verse";

  constructor(engine: AudioEngine, cfg?: BeatConfig) {
    this.engine = engine;
    if (cfg?.bpm) this.bpm = cfg.bpm;
    if (typeof cfg?.swing === "number") this.swing = cfg.swing;
    if (typeof cfg?.kickGain === "number") this.kickGain = cfg.kickGain;
    if (typeof cfg?.snareGain === "number") this.snareGain = cfg.snareGain;
    if (typeof cfg?.hatGain === "number") this.hatGain = cfg.hatGain;
    if (typeof cfg?.kickPunch === "number") this.kickPunch = cfg.kickPunch;
  }

  setBpm(bpm: number): void {
    this.bpm = Math.max(40, Math.min(220, bpm));
  }

  getBpm(): number {
    return this.bpm;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  // Hand the drum kit a new section. Takes effect on the next 16th, so
  // mid-bar swaps don't truncate already-scheduled hits.
  setSection(kind: DrumSectionKind): void {
    this.section = kind;
  }

  getSection(): DrumSectionKind {
    return this.section;
  }

  // Per-song kit tweaks. Called by the MusicDirector when a new song
  // loads so each level's drums have a slightly different colour
  // without us authoring multiple ProceduralBeat subclasses.
  setKit(cfg: BeatConfig): void {
    if (typeof cfg.kickGain === "number") this.kickGain = cfg.kickGain;
    if (typeof cfg.snareGain === "number") this.snareGain = cfg.snareGain;
    if (typeof cfg.hatGain === "number") this.hatGain = cfg.hatGain;
    if (typeof cfg.kickPunch === "number") this.kickPunch = cfg.kickPunch;
    if (typeof cfg.swing === "number") this.swing = cfg.swing;
  }

  async start(): Promise<void> {
    await this.engine.start();
    const ctx = this.engine.context;
    if (!ctx) return;
    if (!this.noiseBuffer) {
      this.noiseBuffer = this.makeNoiseBuffer(ctx, 0.5);
    }
    this.nextNoteTime = ctx.currentTime + 0.05;
    this.step = 0;
    this.barCount = 0;
    this.playing = true;
    this.tick();
  }

  stop(): void {
    this.playing = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  silenceFor(beats: number): void {
    if (!this.playing) return;
    const ctx = this.engine.context;
    if (!ctx) return;
    this.playing = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const seconds = (60 / this.bpm) * beats;
    window.setTimeout(() => {
      if (this.playing) return;
      const ctx2 = this.engine.context;
      if (!ctx2) return;
      this.nextNoteTime = ctx2.currentTime + 0.02;
      this.step = 0;
      this.barCount = 0;
      this.playing = true;
      this.tick();
    }, seconds * 1000);
  }

  // Schedule a one-shot cymbal crash. `time` defaults to the next beat.
  // Used by the Composer wiring to mark section boundaries.
  triggerCrash(time?: number, gain = 0.6): void {
    const ctx = this.engine.context;
    if (!ctx || !this.noiseBuffer) return;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!dest) return;
    const t = time ?? Math.max(this.nextNoteTime, ctx.currentTime + 0.01);
    this.scheduleCrash(t, gain);
  }

  private tick = (): void => {
    if (!this.playing) return;
    const ctx = this.engine.context;
    if (!ctx) return;
    while (this.nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD) {
      this.scheduleStep(this.step, this.nextNoteTime);
      this.advance();
    }
    this.timer = window.setTimeout(this.tick, LOOKAHEAD_MS);
  };

  private advance(): void {
    const sixteenth = 60 / this.bpm / 4;
    const swingOffset =
      this.step % 2 === 1 ? sixteenth * this.swing * 0.3 : 0;
    this.nextNoteTime += sixteenth + swingOffset;
    this.step = (this.step + 1) % 16;
    if (this.step === 0) this.barCount += 1;
  }

  private scheduleStep(step: number, time: number): void {
    const pattern = PATTERNS[this.section];
    const k = pattern.kicks[step]!;
    const s = pattern.snares[step]!;
    const h = pattern.hats[step]!;
    const isRollBar =
      pattern.rollOnEvery4thBar === true && this.barCount % 4 === 3;

    if (k === "K") this.scheduleKick(time, this.kickGain);
    else if (k === "k") this.scheduleKick(time, this.kickGain * 0.45);

    // Snare roll overrides the snare lane on the roll bar so it doesn't
    // double-fire with the pattern's S on step 12.
    if (isRollBar && step >= 12) {
      const t = (step - 12) / 3;
      const rollGain = 0.5 + t * 0.6;
      this.scheduleSnare(time, rollGain);
    } else if (s === "S") {
      this.scheduleSnare(time, 1);
    }

    if (h === "H") this.scheduleHat(time, false);
    else if (h === "h") this.scheduleHat(time, true);

    // Convenience flags for downstream layers. We treat any kick column
    // (K or k) as isKick and any snare column as isSnare so layers stay
    // pattern-agnostic.
    const isKick = k === "K" || k === "k";
    const isSnare = s === "S" || (isRollBar && step >= 12);

    this.onStep.emit({
      step,
      time,
      bpm: this.bpm,
      isKick,
      isSnare,
      isBarStart: step === 0,
    });
  }

  private scheduleKick(time: number, gainScale: number): void {
    const ctx = this.engine.context!;
    const dest = this.engine.musicDestination ?? this.engine.destination!;
    // Punchier 808-flavored kick: longer pitch sweep + more sub presence.
    // Two layers: a low sine "thump" and a tiny click attack.
    const decay = 0.34 * this.kickPunch;
    const sweepEnd = 0.16 * this.kickPunch;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(180, time);
    osc.frequency.exponentialRampToValueAtTime(38, time + sweepEnd);
    gain.gain.setValueAtTime(gainScale, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + decay);
    osc.connect(gain).connect(dest);
    osc.start(time);
    osc.stop(time + decay + 0.02);

    // Click transient for cut-through.
    const click = ctx.createBufferSource();
    click.buffer = this.noiseBuffer;
    const clickHP = ctx.createBiquadFilter();
    clickHP.type = "highpass";
    clickHP.frequency.value = 2500;
    const clickGain = ctx.createGain();
    clickGain.gain.setValueAtTime(gainScale * 0.35, time);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.025);
    click.connect(clickHP).connect(clickGain).connect(dest);
    click.start(time);
    click.stop(time + 0.04);

    // Side-chain pump on every kick. Lighter ducking on ghost kicks.
    this.engine.scheduleDuck(time, 0.35 * gainScale, 4, 220);
  }

  private scheduleSnare(time: number, gainScale: number): void {
    const ctx = this.engine.context!;
    const dest = this.engine.musicDestination ?? this.engine.destination!;
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "highpass";
    noiseFilter.frequency.value = 1500;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(this.snareGain * gainScale, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
    noise.connect(noiseFilter).connect(noiseGain).connect(dest);
    noise.start(time);
    noise.stop(time + 0.2);

    const tone = ctx.createOscillator();
    const toneGain = ctx.createGain();
    tone.type = "triangle";
    tone.frequency.value = 220;
    toneGain.gain.setValueAtTime(this.snareGain * gainScale * 0.4, time);
    toneGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.12);
    tone.connect(toneGain).connect(dest);
    tone.start(time);
    tone.stop(time + 0.14);

    // Clap layer for body on full hits.
    if (gainScale >= 0.9) {
      const clap = ctx.createBufferSource();
      clap.buffer = this.noiseBuffer;
      const clapBP = ctx.createBiquadFilter();
      clapBP.type = "bandpass";
      clapBP.frequency.value = 1800;
      clapBP.Q.value = 1.2;
      const clapGain = ctx.createGain();
      clapGain.gain.setValueAtTime(this.snareGain * gainScale * 0.5, time + 0.005);
      clapGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.09);
      clap.connect(clapBP).connect(clapGain).connect(dest);
      clap.start(time + 0.005);
      clap.stop(time + 0.1);
    }
  }

  private scheduleHat(time: number, soft: boolean): void {
    const ctx = this.engine.context!;
    const dest = this.engine.musicDestination ?? this.engine.destination!;
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 6000;
    const g = ctx.createGain();
    const amp = soft ? this.hatGain * 0.5 : this.hatGain;
    g.gain.setValueAtTime(amp, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.06);
    noise.connect(filter).connect(g).connect(dest);
    noise.start(time);
    noise.stop(time + 0.08);
  }

  // Cymbal crash: a long noise burst through a bandpass biased to ~5kHz
  // with a slow exponential decay. Sounds about as close to a real
  // cymbal as you can get without an actual sample.
  private scheduleCrash(time: number, gainScale: number): void {
    const ctx = this.engine.context!;
    const dest = this.engine.musicDestination ?? this.engine.destination!;
    if (!this.noiseBuffer) return;
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "highpass";
    bp.frequency.value = 4500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gainScale, time);
    // Short attack then long exponential tail (~1.4s).
    g.gain.linearRampToValueAtTime(gainScale, time + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 1.4);
    noise.connect(bp).connect(g).connect(dest);
    noise.start(time);
    noise.stop(time + 1.5);
  }

  private makeNoiseBuffer(ctx: AudioContext, durationSec: number): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * durationSec);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  dispose(): void {
    this.stop();
    this.onStep.clear();
    this.noiseBuffer = null;
  }
}
